import React from 'react';
import PropTypes from 'prop-types';

import LocationCardFulfillmentMethod from 'components/Location/LocationCardFulfillmentMethod';
import LocationCardFulfillmentHours from 'bento-ordering/locations/components/LocationCardFulfillmentHours';
import NoAsapAvailableOrderInfo from 'bento-ordering/locations/components/NoAsapAvailableOrderInfo';
import { fulfillmentOptions } from 'constants/fulfillmentOptions';
import LocationCardFulfillmentTypeGridItem from 'components/Location/LocationCardFulfillmentTypeGridItem';
import { getFormattedDayOfWeek } from 'bento-ordering/base/utils/dates';
import { shouldDisplayNoAsapAvailableOrderInfo } from 'bento-ordering/locations/utils/shouldDisplayNoAsapAvailableOrderInfo';
import MenuItemDescriptionTypography from 'components/MenuItemDescriptionTypography';
import { ITEM_DESCRIPTION_AND_PRICES } from 'styles/constants/appearanceSectionNames';
import LocationCardPreviewMenu from 'bento-ordering/locations/components/LocationCardPreviewMenu';
import useIsPreviewMenuOn from 'hooks/useIsPreviewMenuOn';

export const NOT_ACCEPTING_ORDERS_INFORMATION_TEXT =
  'No pickup or delivery is offered for this location';

export const NO_ORDERS_BEING_ACCEPTED_AT_THIS_TIME =
  'No orders are being accepted at this time';

/**
 * Renders available fulfillment options(CTA) and availability for online ordering.
 *
 * @param isClosed
 * @param isAcceptingDeliveryOrders
 * @param isAcceptingPickupOrders
 * @param isAsapDeliveryCurrentlyAvailable
 * @param isAsapPickupCurrentlyAvailable
 * @param beginOrder
 * @param id
 * @param firstAvailablePickupDateOption
 * @param openDisplayDeliveryHours
 * @param firstAvailableDeliveryDateOption
 * @param doesDeliverToAddress
 * @param openDisplayPickupHours
 * @returns {*}
 */
const LocationCardOnlineOrderingFulfillmentMethods = ({
  isClosed,
  isAcceptingDeliveryOrders,
  isAcceptingPickupOrders,
  isAsapDeliveryCurrentlyAvailable,
  isAsapPickupCurrentlyAvailable,
  beginOrder,
  id,
  firstAvailablePickupDateOption,
  openDisplayDeliveryHours,
  firstAvailableDeliveryDateOption,
  doesDeliverToAddress,
  openDisplayPickupHours,
  isAcceptingLaterOrders,
  firstAvailableNoAsapPickupDateOption,
  firstAvailableNoAsapDeliveryDateOption,
  isAcceptingOrders
}) => {
  const isPreviewMenuOn = useIsPreviewMenuOn({
    accepting_orders: isAcceptingOrders,
    is_closed: isClosed
  });

  const shouldDisplayNoAsapAvailableOrderInfoDeliveryOrders = shouldDisplayNoAsapAvailableOrderInfo(
    isAcceptingDeliveryOrders,
    isAcceptingLaterOrders,
    openDisplayDeliveryHours
  );
  const shouldDisplayNoAsapAvailableOrderInfoPickupOrders = shouldDisplayNoAsapAvailableOrderInfo(
    isAcceptingPickupOrders,
    isAcceptingLaterOrders,
    openDisplayPickupHours
  );

  if (isClosed) {
    return (
      <LocationCardFulfillmentTypeGridItem>
        <MenuItemDescriptionTypography variant={ITEM_DESCRIPTION_AND_PRICES}>
          {NOT_ACCEPTING_ORDERS_INFORMATION_TEXT}
        </MenuItemDescriptionTypography>
      </LocationCardFulfillmentTypeGridItem>
    );
  }

  if (isPreviewMenuOn) {
    return (
      <LocationCardPreviewMenu
        beginOrder={beginOrder}
        isAcceptingPickupOrders={isAcceptingPickupOrders}
        isAcceptingDeliveryOrders={isAcceptingDeliveryOrders}
      />
    );
  }

  return (
    <React.Fragment>
      {isAcceptingDeliveryOrders && (
        <LocationCardFulfillmentMethod
          isFullWidth
          buttonLabel={
            isAsapDeliveryCurrentlyAvailable ? 'Delivery' : 'Delivery Later'
          }
          handleButtonClick={() => beginOrder(fulfillmentOptions.DELIVERY)}
          disableButton={!doesDeliverToAddress}
          hoursType={fulfillmentOptions.DELIVERY}
          buttonDataTestId={`location-${id}-delivery-button`}
          LocationCardFulfillmentHours={
            <LocationCardFulfillmentHours
              hours={openDisplayDeliveryHours.today.hours}
              weekdayHours={openDisplayDeliveryHours.weekday_hours}
              hoursDataTestId={`location-${id}-delivery-hours`}
              hoursLabel={
                isAsapDeliveryCurrentlyAvailable
                  ? "Today's Delivery Hours"
                  : `Next Delivery ${getFormattedDayOfWeek(
                      firstAvailableDeliveryDateOption
                    )}`
              }
            />
          }
        />
      )}

      {shouldDisplayNoAsapAvailableOrderInfoDeliveryOrders && (
        <NoAsapAvailableOrderInfo
          fulfillmentMethod={fulfillmentOptions.DELIVERY}
          LocationCardFulfillmentHours={
            <LocationCardFulfillmentHours
              hours={
                openDisplayDeliveryHours.first_available_asap
                  .first_available_time_ranges
              }
              weekdayHours={openDisplayDeliveryHours.weekday_hours}
              hoursDataTestId={`location-${id}-delivery-hours`}
              hoursLabel={
                isAsapDeliveryCurrentlyAvailable
                  ? "Today's Delivery Hours"
                  : `Next Delivery ${getFormattedDayOfWeek(
                      firstAvailableNoAsapDeliveryDateOption
                    )}`
              }
            />
          }
        />
      )}

      {isAcceptingPickupOrders && (
        <LocationCardFulfillmentMethod
          isFullWidth
          buttonLabel={
            isAsapPickupCurrentlyAvailable ? 'Pickup' : 'Pickup Later'
          }
          handleButtonClick={() => beginOrder(fulfillmentOptions.PICKUP)}
          hours={openDisplayPickupHours}
          hoursType={fulfillmentOptions.PICKUP}
          buttonDataTestId={`location-${id}-pickup-button`}
          LocationCardFulfillmentHours={
            <LocationCardFulfillmentHours
              hours={openDisplayPickupHours.today.hours}
              weekdayHours={openDisplayPickupHours.weekday_hours}
              hoursDataTestId={`location-${id}-pickup-hours`}
              hoursLabel={
                isAsapPickupCurrentlyAvailable
                  ? "Today's Pickup Hours"
                  : `Next Pickup ${getFormattedDayOfWeek(
                      firstAvailablePickupDateOption
                    )}`
              }
            />
          }
        />
      )}

      {shouldDisplayNoAsapAvailableOrderInfoPickupOrders && (
        <NoAsapAvailableOrderInfo
          fulfillmentMethod={fulfillmentOptions.PICKUP}
          LocationCardFulfillmentHours={
            <LocationCardFulfillmentHours
              hours={
                openDisplayPickupHours.first_available_asap
                  .first_available_time_ranges
              }
              weekdayHours={openDisplayPickupHours.weekday_hours}
              hoursDataTestId={`location-${id}-pickup-hours`}
              hoursLabel={
                isAsapPickupCurrentlyAvailable
                  ? "Today's Pickup Hours"
                  : `Next Pickup ${getFormattedDayOfWeek(
                      firstAvailableNoAsapPickupDateOption
                    )}`
              }
            />
          }
        />
      )}
    </React.Fragment>
  );
};

LocationCardOnlineOrderingFulfillmentMethods.propTypes = {
  beginOrder: PropTypes.func.isRequired,
  doesDeliverToAddress: PropTypes.bool,
  firstAvailablePickupDateOption: PropTypes.string,
  firstAvailableDeliveryDateOption: PropTypes.string,
  id: PropTypes.number.isRequired,
  isAcceptingDeliveryOrders: PropTypes.bool,
  isAcceptingPickupOrders: PropTypes.bool,
  isAsapDeliveryCurrentlyAvailable: PropTypes.bool,
  isAsapPickupCurrentlyAvailable: PropTypes.bool,
  isClosed: PropTypes.bool,
  openDisplayDeliveryHours: PropTypes.shape({
    today: PropTypes.shape({
      hours: PropTypes.array
    }),
    weekday_hours: PropTypes.array,
    first_available_asap: PropTypes.shape({
      first_available_time_ranges: PropTypes.array
    })
  }),
  openDisplayPickupHours: PropTypes.shape({
    today: PropTypes.shape({
      hours: PropTypes.array
    }),
    weekday_hours: PropTypes.array,
    first_available_asap: PropTypes.shape({
      first_available_time_ranges: PropTypes.array
    })
  }),
  isAcceptingLaterOrders: PropTypes.bool.isRequired,
  firstAvailableNoAsapPickupDateOption: PropTypes.string,
  firstAvailableNoAsapDeliveryDateOption: PropTypes.string,
  isAcceptingOrders: PropTypes.bool.isRequired
};

LocationCardOnlineOrderingFulfillmentMethods.defaultProps = {
  doesDeliverToAddress: true,
  firstAvailablePickupDateOption: '',
  firstAvailableDeliveryDateOption: '',
  isAcceptingPickupOrders: true,
  isAcceptingDeliveryOrders: true,
  isAsapDeliveryCurrentlyAvailable: true,
  isAsapPickupCurrentlyAvailable: true,
  isClosed: false
};

export default LocationCardOnlineOrderingFulfillmentMethods;
